<template>
  <div class="w-full space-y-7 pb-7">
    <div
      class="flex flex-col items-center space-y-8 select-none"
      :class="size === 'large' ? 'px-2' : 'px-7'"
    >
      <div
        :class="[
          'relative overflow-hidden rounded-xl border border-black',
          size === 'large'
            ? 'h-[400px] min-h-[400px] w-full'
            : 'h-[300px] min-h-[300px] w-[300px] min-w-[300px] lg:h-[350px] lg:min-h-[350px] lg:w-[350px] lg:min-w-[350px]',
        ]"
      >
        <loader :is-loading="imageLoading" />
        <img class="block max-w-full" :src="avatarUrl" @load="initCropper" />

        <div
          class="absolute bottom-5 left-1/2 flex -translate-x-1/2 transform items-center justify-center space-x-1 rounded-full bg-disabled py-1 pr-2-5 pl-2"
        >
          <icon-base :icon="IconMove" />
          <p class="translate-y-px text-xs font-medium text-gray-500">
            {{ $t("COMMON.MOVE").toUpperCase() }}
          </p>
        </div>
      </div>

      <div class="flex w-full items-center space-x-4">
        <div
          class="buttons-wrapper bg-gray-100"
          @click.stop.prevent="scaleAvatar(-0.1)"
        >
          <icon-base
            :icon="IconSubtract"
            class="-translate-x-px -translate-y-[2.5px] transform cursor-pointer"
          />
        </div>

        <div class="w-full">
          <lf-slider
            v-model="scale"
            class="slider"
            :min="MIN_SCALE"
            :max="MAX_SCALE"
            :interval="0.05"
            :lazy="false"
          />
        </div>

        <div
          class="buttons-wrapper bg-gray-100"
          @click.stop.prevent="scaleAvatar(0.1)"
        >
          <icon-base
            :icon="IconPlusSmall"
            class="-translate-y-px transform cursor-pointer"
          />
        </div>

        <div class="buttons-wrapper" @click.stop.prevent="rotateAvatar">
          <icon-base
            class="translate-x-px -translate-y-px transform cursor-pointer text-gray-400"
            :icon="IconReload"
            height="24"
            width="24"
          />
        </div>
      </div>

      <div v-if="preview" class="flex w-full flex-col space-y-2">
        <div class="space-x-2 font-medium text-gray-600">
          <span>{{ $t("COMMON.PREVIEW") }}</span>
          <icon-base
            v-tooltip="$t('COMMON.LOGO_PREVIEW_TOOLTIP')"
            :icon="IconInfo"
            class="text-gray-400"
          />
        </div>

        <div class="flex items-end space-x-4">
          <div
            class="image-preview h-12 min-h-12 w-12 min-w-12 overflow-hidden rounded-xs border"
          ></div>
          <div
            class="image-preview h-9 min-h-9 w-9 min-w-9 overflow-hidden rounded-xs border"
          ></div>
          <div
            class="image-preview h-6 min-h-6 w-6 min-w-6 overflow-hidden rounded-xs border"
          ></div>
        </div>
      </div>
    </div>

    <div class="flex w-full justify-end border-t pt-7">
      <modal-buttons :on-save="saveAvatar" :on-cancel="() => $emit('cancel')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

import IconInfo from "../icons/IconInfo.vue";
import IconMove from "@/components/icons/IconMove.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconReload from "@/components/icons/IconReload.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfSlider from "@/components/ui/LfSlider.vue";
import Loader from "@/components/ui/Loader.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

const MIN_SCALE = 1 as const;
const MAX_SCALE = 3 as const;

const emit = defineEmits<{ save: [File]; cancel: [] }>();
const {
  avatar,
  size = "default",
  preview = true,
  aspectRatio,
} = defineProps<{
  avatar: File;
  size?: "default" | "large";
  preview?: boolean;
  aspectRatio?: number;
}>();

const cropper = ref<Cropper | null>(null);
const imageLoading = ref(true);
const avatarUrl = URL.createObjectURL(avatar);

const initCropper = (event: Event) => {
  const image = event.target as HTMLImageElement;
  cropper.value = new Cropper(image, {
    aspectRatio: aspectRatio ?? NaN,
    viewMode: 1,
    dragMode: "move",
    guides: false,
    center: false,
    zoomable: false,
    autoCropArea: 1,
    preview: ".image-preview",
    ready: () => {
      imageLoading.value = false;
    },
  });
};

const scale = computed({
  // Both X and Y scales are the same, so we can get either one of them
  get: () => cropper.value?.getData().scaleX ?? MIN_SCALE,
  set: (value) => {
    cropper.value?.scale(value, value);
  },
});

const scaleAvatar = (value: number) => {
  const nextScale = scale.value + value;

  if (nextScale < scale.value && nextScale < MIN_SCALE) {
    scale.value = MIN_SCALE;
    return;
  }

  if (nextScale > scale.value && nextScale > MAX_SCALE) {
    scale.value = MAX_SCALE;
    return;
  }

  scale.value = nextScale;
};

const rotateAvatar = () => {
  cropper.value?.rotate(-90);
};

const saveAvatar = () => {
  cropper.value?.getCroppedCanvas().toBlob((blob) => {
    const file = new File([blob as NonNullable<typeof blob>], "avatar.jpg", {
      type: "image/jpeg",
    });
    emit("save", file);
  });
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.buttons-wrapper {
  @apply flex h-6 min-h-6 w-6 min-w-6 items-center justify-center rounded-full;
}

.slider :deep() {
  & .connect {
    @apply bg-gray-400;
  }

  & .handle {
    @apply border-gray-400;
  }

  & .base {
    @apply bg-gray-400;
  }

  & .base {
    @apply h-1;
  }
}
</style>

<style>
@reference "@/assets/tailwind.css";

.cropper-point {
  @apply bg-transparent;
}

.cropper-face {
  @apply bg-transparent;
}

.cropper-view-box {
  @apply rounded-xl border-2 border-dashed border-divider outline-none;
}

.cropper-line {
  @apply bg-transparent;
}
</style>
